// src/views/Tarjetas/NuevaTarjeta/TarjetaPrepaga.jsx
import React from "react";
import ReactDOM from "react-dom";
import { FaSave, FaArrowLeft } from "react-icons/fa";
import apiRoutes from "../../../api/apiRoutes";
import { apiFetchJson } from "../../../utils/http";
import PermisoTarjeta from "../../Tarjetas/Configuracion/PermisoTarjeta";
import ClienteAutocomplete from "../../../components/autocomplete/ClienteAutocomplete";
import EessMultiSelect from "../../../components/multiselect/EessMultiSelect";
import InputNumberNoScroll from "../../../components/inputs/inputNumberNoScroll";

function TarjetaPrepaga({ onClose = () => {} }) {
  /* ====== Estado ====== */
  // 1) Cliente — autocomplete
  const [clienteId, setClienteId] = React.useState("");
  const [clienteSeleccionado, setClienteSeleccionado] = React.useState(null);

  const abortRef = React.useRef(null);

  // 2) Vehículo
  const [patente, setPatente] = React.useState("");
  const [descripcion, setDescripcion] = React.useState("");
  const [marca, setMarca] = React.useState("");

  // Marca — autocomplete
  const [marcaId, setMarcaId] = React.useState("");
  const [marcaInput, setMarcaInput] = React.useState("");
  const [openMarcaBox, setOpenMarcaBox] = React.useState(false);

  const [marcaItems, setMarcaItems] = React.useState([]);
  const [marcaPage, setMarcaPage] = React.useState(0);
  const [marcaHasMore, setMarcaHasMore] = React.useState(false);
  const [marcaLoading, setMarcaLoading] = React.useState(false);

  const reqSeqMarcaRef = React.useRef(0);
  const sizeMarcas = 50;

  // 3) Centro de costo / Observaciones
  const [centroCosto, setCentroCosto] = React.useState("");
  const [obs, setObs] = React.useState("");

  // 4) Capacidad del tanque
  const [capacidadTanque, setCapacidadTanque] = React.useState("");

  // 5) Estaciones
  const [estSelectedIds, setEstSelectedIds] = React.useState([]);

  // ===== Permisos (modal) =====
  const [mostrarPermisos, setMostrarPermisos] = React.useState(false);
  const [tarjetaSel] = React.useState(null);

  // ===== Overlay (popup) =====
  // mode: "hidden" | "loading" | "success" | "error"
  const [overlay, setOverlay] = React.useState({
    open: false,
    mode: "hidden",
    msg: "",
    numero: null,
    id: null,
  });

  /* ====== Validación ====== */
  const [errors, setErrors] = React.useState({});
  const [alerta, setAlerta] = React.useState("");

  const validateRequired = () => {
    const newErr = {};
    // 1) Cliente
    if (!clienteId) newErr.clienteId = "Debe seleccionar un cliente.";
    // 2) Vehículo
    if (!patente?.trim()) newErr.patente = "La patente es obligatoria.";
    if (!descripcion?.trim())
      newErr.descripcion = "La descripción/modelo es obligatoria.";
    if (!marca) newErr.marca = "Debe seleccionar una marca.";
    // 4) Capacidad tanque (si viene, no negativa)
    if (capacidadTanque !== "" && Number(capacidadTanque) < 0) {
      newErr.capacidadTanque = "La capacidad del tanque no puede ser negativa.";
    }
    // 5) Estaciones — al menos una
    if (!estSelectedIds || estSelectedIds.length === 0) {
      newErr.eess = "Debe seleccionar al menos una estación.";
    }
    setErrors(newErr);
    setAlerta(
      Object.keys(newErr).length
        ? "Debes completar correctamente los campos obligatorios de 1) Cliente, 2) Vehículo y 5) Estaciones."
        : "",
    );
    return newErr;
  };

  const touchRequired = () => {
    const miss = {};
    if (!clienteId) miss.clienteId = "Debe seleccionar un cliente.";
    if (!patente?.trim()) miss.patente = "La patente es obligatoria.";
    if (!descripcion?.trim())
      miss.descripcion = "La descripción/modelo es obligatoria.";
    if (!marca) miss.marca = "Debe seleccionar una marca.";
    if (!estSelectedIds || estSelectedIds.length === 0)
      miss.eess = "Debe seleccionar al menos una estación.";
    if (Object.keys(miss).length) {
      setErrors((p) => ({ ...p, ...miss }));
      setAlerta(
        "Debes completar correctamente los campos obligatorios de 1) Cliente, 2) Vehículo y 5) Estaciones.",
      );
    }
  };

  /* ====== (1) Cliente — llamadas de red ====== */

  // === Marcas: search y browse ===
  const fetchMarcasSearch = React.useCallback(
    async ({ q, page }) => {
      try {
        abortRef.current?.abort();
      } catch {}
      const ctrl = new AbortController();
      abortRef.current = ctrl;

      const seq = ++reqSeqMarcaRef.current;

      if (!page || page === 0) {
        setMarcaItems([]);
        setMarcaHasMore(false);
      }
      setMarcaLoading(true);

      try {
        const endpoint =
          `${apiRoutes.vehiculosConTarjeta.autocomplete}` +
          `?tipo=marca&q=${encodeURIComponent(q ?? "")}` +
          `&page=${encodeURIComponent(page ?? 0)}` +
          `&size=${encodeURIComponent(sizeMarcas)}`;

        const data = await apiFetchJson(endpoint, { signal: ctrl.signal });
        if (seq !== reqSeqMarcaRef.current) return;

        const mapped = (data?.items ?? []).map((m) => ({
          id: m.id,
          nombre: m.marcaNombre ?? "",
        }));

        setMarcaItems((prev) =>
          page && page > 0 ? [...prev, ...mapped] : mapped,
        );
        setMarcaHasMore(Boolean(data?.hasMore));
        setMarcaPage(data?.page ?? page ?? 0);
      } catch (e) {
        if (e.name !== "AbortError") {
          console.error("Autocomplete marcas:", e);
          setMarcaItems([]);
          setMarcaHasMore(false);
        }
      } finally {
        setMarcaLoading(false);
      }
    },
    [sizeMarcas],
  );

  const fetchMarcasBrowse = React.useCallback(
    async ({ page }) => {
      try {
        abortRef.current?.abort();
      } catch {}
      const ctrl = new AbortController();
      abortRef.current = ctrl;

      const seq = ++reqSeqMarcaRef.current;

      if (!page || page === 0) {
        setMarcaItems([]);
        setMarcaHasMore(false);
      }
      setMarcaLoading(true);

      try {
        const endpoint =
          `${apiRoutes.vehiculosConTarjeta.autocomplete}` +
          `?tipo=marca&q=&page=${encodeURIComponent(page ?? 0)}` +
          `&size=${encodeURIComponent(sizeMarcas)}`;

        const data = await apiFetchJson(endpoint, { signal: ctrl.signal });

        if (seq !== reqSeqMarcaRef.current) return;

        const mapped = (data?.items ?? []).map((m) => ({
          id: m.id,
          nombre: m.marcaNombre ?? "",
        }));

        setMarcaItems((prev) =>
          page && page > 0 ? [...prev, ...mapped] : mapped,
        );
        setMarcaHasMore(Boolean(data?.hasMore));
        setMarcaPage(data?.page ?? page ?? 0);
      } catch (e) {
        if (e.name !== "AbortError") {
          console.error("Browse marcas:", e);
          setMarcaItems([]);
          setMarcaHasMore(false);
        }
      } finally {
        setMarcaLoading(false);
      }
    },
    [sizeMarcas],
  );

  // al escribir en Marca
  React.useEffect(() => {
    if (!openMarcaBox) return;
    const q = (marcaInput ?? "").trim();
    const t = setTimeout(() => {
      if (q) fetchMarcasSearch({ q, page: 0 });
      else fetchMarcasBrowse({ page: 0 });
    }, 250);
    return () => clearTimeout(t);
  }, [marcaInput, openMarcaBox, fetchMarcasSearch, fetchMarcasBrowse]);

  const selectMarca = (opt) => {
    setMarcaId(String(opt.id));
    setMarcaInput(opt.nombre);
    setMarca(opt.nombre);
    setErrors((p) => ({ ...p, marca: undefined }));
    setOpenMarcaBox(false);
  };

  const handleClienteChange = (cliente) => {
    setClienteSeleccionado(cliente);
    if (cliente) {
      setClienteId(String(cliente.id));
    } else {
      setClienteId("");
    }
  };

  /* ====== Guardar ====== */
  const getNumeroTarjeta = (resp) => {
    const r = resp || {};
    return (
      r.numeroTarjeta ??
      r.tarjetaNumero ??
      r.nroTarjeta ??
      r.tarjeta ??
      r.numero ??
      r.idTarjeta ??
      r?.data?.numeroTarjeta ??
      r?.data?.tarjetaNumero ??
      r?.data?.tarjeta ??
      null
    );
  };

  const handleGuardar = async () => {
    const errs = validateRequired();
    if (Object.keys(errs).length > 0) {
      const firstKey = Object.keys(errs)[0];
      const el = document.querySelector(`[data-field="${firstKey}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    const payload = {
      clienteId: clienteId ? Number(clienteId) : null,
      patente: (patente ?? "").trim(),
      vehiculoDetalles: (descripcion ?? "").trim(),
      vehiculoMarcaId: marcaId ? Number(marcaId) : null,
      capacidadTanque:
        capacidadTanque === "" || capacidadTanque === null
          ? null
          : Number(capacidadTanque),
      centroCosto: (centroCosto ?? "").trim() || null,
      observaciones: (obs ?? "").trim() || null,
      eessIds: Array.from(estSelectedIds).map((id) => Number(id)),
    };

    try {
      setAlerta("");
      setOverlay({
        open: true,
        mode: "loading",
        msg: "Generando solicitud…",
        numero: null,
        id: null,
      });

      const resp = await apiFetchJson(apiRoutes.tarjetas.solicitarPrepaga, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      const numero = getNumeroTarjeta(resp);
      const idTarjeta = resp?.id ?? resp?.tarjetaId ?? resp?.data?.id ?? null;

      setOverlay({
        open: true,
        mode: "success",
        msg: numero
          ? `Tarjeta creada: ${numero}`
          : "Operación realizada correctamente.",
        numero: numero ?? null,
        id: idTarjeta ?? null,
      });
    } catch (e) {
      console.error("Error al solicitar tarjeta prepaga:", e);
      setOverlay({
        open: true,
        mode: "error",
        msg: "No se pudo crear la tarjeta prepaga. Verifica los datos e intenta nuevamente.",
        numero: null,
        id: null,
      });
    }
  };

  // Habilitar Guardar solo si cumple obligatorios
  const isGuardarEnabled = React.useMemo(() => {
    const okCliente = !!clienteId;
    const okPatente = !!(patente && patente.trim());
    const okDesc = !!(descripcion && descripcion.trim());
    const okMarca = !!marca;
    const capOk = capacidadTanque === "" || Number(capacidadTanque) >= 0;
    const okEess = estSelectedIds && estSelectedIds.length > 0;
    const bloqueoPopup = overlay.open && overlay.mode === "loading";
    return (
      okCliente &&
      okPatente &&
      okDesc &&
      okMarca &&
      capOk &&
      okEess &&
      !bloqueoPopup
    );
  }, [
    clienteId,
    patente,
    descripcion,
    marca,
    capacidadTanque,
    estSelectedIds,
    overlay.open,
    overlay.mode,
  ]);

  // === Abrir permisos con la tarjeta recién creada
  const abrirPermisosConTarjetaCreada = React.useCallback(() => {
    onClose();
  }, [onClose]);

  React.useEffect(() => {
    if (!mostrarPermisos) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mostrarPermisos]);

  function ModalPortal({ children }) {
    return ReactDOM.createPortal(children, document.body);
  }

  /* ====== UI ====== */
  return (
    <div className="page">
      <div className="mb-2 flex items-start justify-between">
        <h2 className="page-title">
          Introduzca los datos de Solicitar una nueva Tarjeta
        </h2>
      </div>

      {/* Alerta global */}
      {alerta && (
        <div className="mb-4 rounded-md border border-red-200 bg-red-50 p-3 text-red-700 text-sm">
          {alerta}
        </div>
      )}

      {/* 1) Seleccione Cliente */}
      <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-2">
        1. Seleccione Cliente
      </h3>
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 md:p-5 mb-4">
        <div className="space-y-4 text-sm md:text-base">
          <div className="grid grid-cols-1 md:grid-cols-[160px_1fr] items-start gap-3">
            <label className="text-gray-700 font-medium">Cliente:</label>
            <div className="w-full" data-field="clienteId">
              <ClienteAutocomplete
                value={clienteSeleccionado}
                onChange={handleClienteChange}
                placeholder="Buscar cliente..."
              />
              {errors.clienteId && (
                <p className="mt-1 text-xs text-red-600">{errors.clienteId}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 2) Seleccione Vehículo */}
      <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-2">
        2. Seleccione Vehículo
      </h3>
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 md:p-5 mb-4">
        <div className="space-y-4 text-sm md:text-base">
          <div className="grid grid-cols-1 md:grid-cols-[160px_1fr] items-center gap-3">
            <label className="text-gray-700">Patente:</label>
            <div data-field="patente" className="w-full">
              <input
                className={`border rounded-md px-3 py-2 bg-white w-full ${
                  errors.patente ? "border-red-500" : "border-gray-300"
                }`}
                style={{ textTransform: "uppercase" }}
                value={patente}
                onFocus={touchRequired}
                onChange={(e) => {
                  setPatente(e.target.value.toUpperCase());
                  setErrors((prev) => ({ ...prev, patente: undefined }));
                }}
              />
              {errors.patente && (
                <p className="mt-1 text-xs text-red-600">{errors.patente}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[160px_240px] items-center gap-3">
            <label className="text-gray-700">Marca:</label>
            <div data-field="marca" className="relative w-full">
              <input
                type="text"
                placeholder="Escribe para buscar…"
                className={`w-full border rounded-md px-3 py-2 bg-white ${
                  errors.marca ? "border-red-500" : "border-gray-300"
                }`}
                style={{ textTransform: "uppercase" }}
                value={marcaInput}
                onFocus={() => {
                  setErrors((p) => ({ ...p, marca: undefined }));
                  setOpenMarcaBox(true);
                  if (!(marcaInput ?? "").trim())
                    fetchMarcasBrowse({ page: 0 });
                }}
                onChange={(e) => {
                  const v = e.target.value;
                  setMarcaInput(v);
                  setMarcaId("");
                  setMarca("");
                  setOpenMarcaBox(true);
                  setMarcaItems([]);
                  setMarcaHasMore(false);
                  setMarcaPage(0);
                }}
                onBlur={() => setTimeout(() => setOpenMarcaBox(false), 120)}
              />

              {openMarcaBox && (
                <div
                  className="absolute z-20 mt-1 w-full border rounded bg-white shadow-lg"
                  onMouseDown={(e) => e.preventDefault()}
                >
                  <div className="p-2 border-b bg-gray-50 text-xs text-gray-600">
                    {marcaLoading
                      ? "Cargando…"
                      : (marcaInput ?? "").trim()
                        ? "Resultados"
                        : "Explorando"}
                  </div>

                  <div
                    className="max-h-72 overflow-auto"
                    onScroll={(e) => {
                      const el = e.currentTarget;
                      if (!marcaHasMore || marcaLoading) return;
                      if (
                        el.scrollTop + el.clientHeight >=
                        el.scrollHeight - 10
                      ) {
                        const q = (marcaInput ?? "").trim();
                        if (q)
                          fetchMarcasSearch({ q, page: (marcaPage ?? 0) + 1 });
                        else fetchMarcasBrowse({ page: (marcaPage ?? 0) + 1 });
                      }
                    }}
                  >
                    {marcaItems.map((m) => (
                      <button
                        key={m.id}
                        type="button"
                        className="w-full text-left px-3 py-2 hover:bg-gray-50"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => selectMarca(m)}
                      >
                        <div className="text-sm text-gray-900">{m.nombre}</div>
                      </button>
                    ))}

                    {!marcaLoading && marcaItems.length === 0 && (
                      <div className="px-3 py-3 text-sm text-gray-500">
                        Sin resultados
                      </div>
                    )}

                    {marcaLoading && (
                      <div className="px-3 py-3 text-sm text-gray-500">
                        Cargando…
                      </div>
                    )}
                  </div>

                  {marcaHasMore && !marcaLoading && (
                    <div className="p-2 border-t text-center">
                      <button
                        type="button"
                        className="text-green-700 text-sm hover:underline"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={() => {
                          const q = (marcaInput ?? "").trim();
                          if (q)
                            fetchMarcasSearch({
                              q,
                              page: (marcaPage ?? 0) + 1,
                            });
                          else
                            fetchMarcasBrowse({ page: (marcaPage ?? 0) + 1 });
                        }}
                      >
                        Cargar más
                      </button>
                    </div>
                  )}
                </div>
              )}

              {errors.marca && (
                <p className="mt-1 text-xs text-red-600">{errors.marca}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[160px_1fr] items-start gap-3">
            <label className="text-gray-700 mt-2">Descripción/Modelo:</label>
            <div data-field="descripcion" className="w-full">
              <textarea
                rows={3}
                className={`border rounded-md px-3 py-2 bg-white w-full ${
                  errors.descripcion ? "border-red-500" : "border-gray-300"
                }`}
                style={{ textTransform: "uppercase" }}
                value={descripcion}
                onFocus={touchRequired}
                onChange={(e) => {
                  setDescripcion(e.target.value.toUpperCase());
                  setErrors((prev) => ({ ...prev, descripcion: undefined }));
                }}
              />
              {errors.descripcion && (
                <p className="mt-1 text-xs text-red-600">
                  {errors.descripcion}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* 3) Centro de Costo y Observaciones */}
      <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-2">
        3. Centro de Costo y Observaciones
      </h3>
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 md:p-5 mb-4">
        <div className="space-y-4 text-sm md:text-base">
          <div className="grid grid-cols-1 md:grid-cols-[160px_320px] items-center gap-3">
            <label className="text-gray-700">Centro Costo:</label>
            <input
              className="border border-gray-300 rounded-md px-3 py-2 bg-white"
              style={{ textTransform: "uppercase" }}
              value={centroCosto}
              onFocus={touchRequired}
              onChange={(e) => setCentroCosto(e.target.value.toUpperCase())}
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-[160px_1fr] items-start gap-3">
            <label className="text-gray-700 mt-2">Obs:</label>
            <textarea
              rows={3}
              className="border border-gray-300 rounded-md px-3 py-2 bg-white"
              style={{ textTransform: "uppercase" }}
              value={obs}
              onFocus={touchRequired}
              onChange={(e) => setObs(e.target.value.toUpperCase())}
            />
          </div>
        </div>
      </div>

      {/* 4) Capacidad del tanque */}
      <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-2">
        4. Capacidad del tanque
      </h3>
      <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 md:p-5 mb-4">
        <div
          className="grid grid-cols-1 md:grid-cols-[220px_240px] items-center gap-3 text-sm md:text-base"
          data-field="capacidadTanque"
        >
          <label className="text-gray-700">Capacidad del Tanque:</label>
          <div>
              <InputNumberNoScroll
              className={`border rounded-md px-3 py-2 bg-white w-full ${
                errors.capacidadTanque ? "border-red-500" : "border-gray-300"
              }`}
              value={capacidadTanque}
              onFocus={touchRequired}
              onChange={(e) => {
                const v = e.target.value;
                setCapacidadTanque(v);
                const n = Number(v);
                if (v !== "" && n < 0) {
                  setErrors((p) => ({
                    ...p,
                    capacidadTanque:
                      "La capacidad del tanque no puede ser negativa.",
                  }));
                } else {
                  setErrors((p) => ({ ...p, capacidadTanque: undefined }));
                }
              }}
            />
            {errors.capacidadTanque && (
              <p className="mt-1 text-xs text-red-600">
                {errors.capacidadTanque}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* 5) Estaciones de Servicio Asociadas */}
      <h3 className="text-base md:text-lg font-semibold text-gray-800 mb-2">
        5. Estaciones de Servicio Asociadas
      </h3>
      <div
        className="rounded-lg border border-gray-200 bg-gray-50 p-4 md:p-5 mb-6"
        data-field="eess"
      >
        <label className="text-gray-700">EE.SS:</label>
        <div className="text-sm md:text-base relative w-full md:w-[520px]">
          <EessMultiSelect
            value={estSelectedIds}
            onChange={(ids) => {
              setEstSelectedIds(ids);
              setErrors((prev) => ({ ...prev, eess: undefined }));
            }}
            disabled={overlay.open && overlay.mode === "loading"}
          />
        </div>
        {errors.eess && (
          <p className="mt-2 text-xs text-red-600">{errors.eess}</p>
        )}
      </div>

      {/* Footer global */}
      <div className="mt-4 flex items-center justify-end gap-2">
        <button
          type="button"
          className="btn-outline px-4 py-2"
          onClick={onClose}
        >
          <FaArrowLeft className="mr-2 inline" /> Cancelar
        </button>
        <button
          type="button"
          className={
            isGuardarEnabled ? "btn px-4 py-2" : "btn-fil-disabled px-4 py-2"
          }
          onClick={handleGuardar}
          disabled={!isGuardarEnabled}
        >
          <FaSave className="mr-2 inline" /> Guardar
        </button>
      </div>

      {/* ===== Overlay / Popup ===== */}
      {overlay.open && (
        <div className="fixed inset-0 z-[9999] bg-black/25 backdrop-blur-[1px] flex items-center justify-center">
          <div className="bg-white rounded-2xl shadow-xl px-6 py-5 w-[90%] max-w-md text-center">
            {overlay.mode === "loading" && (
              <>
                <div className="mx-auto mb-3 h-10 w-10 rounded-full border-4 border-gray-300 border-t-transparent animate-spin" />
                <div className="text-gray-800 font-semibold">
                  Generando solicitud…
                </div>
                <div className="mt-1 text-sm text-gray-600">
                  Por favor, espere sin cerrar ni recargar la página.
                </div>
              </>
            )}

            {overlay.mode === "success" && (
              <>
                <div className="mx-auto mb-3 h-10 w-10 rounded-full border-4 border-green-400 flex items-center justify-center">
                  <span className="text-green-600 text-lg">✓</span>
                </div>
                <div className="text-green-700 font-semibold">
                  {overlay.msg}
                </div>
                <div className="mt-4 flex items-center justify-center gap-2">
                  {overlay.numero && (
                    <button
                      type="button"
                      className="btn px-4 py-2"
                      onClick={abrirPermisosConTarjetaCreada}
                    >
                      Cerrar
                    </button>
                  )}
                </div>
              </>
            )}

            {overlay.mode === "error" && (
              <>
                <div className="mx-auto mb-3 h-10 w-10 rounded-full border-4 border-red-400 flex items-center justify-center">
                  <span className="text-red-600 text-lg">!</span>
                </div>
                <div className="text-red-700 font-semibold">{overlay.msg}</div>
                <div className="mt-4">
                  <button
                    type="button"
                    className="btn px-4 py-2"
                    onClick={() =>
                      setOverlay({
                        open: false,
                        mode: "hidden",
                        msg: "",
                        numero: null,
                        id: null,
                      })
                    }
                  >
                    Entendido
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ===== Modal PermisoTarjeta ===== */}
      {mostrarPermisos && (
        <ModalPortal>
          <div className="fixed inset-0 z-[10000] bg-black/40">
            <div className="absolute inset-0 flex items-center justify-center p-4">
              <div className="bg-white w-full max-w-[1200px] h-[90vh] rounded-2xl shadow-2xl overflow-hidden">
                <div className="flex items-center justify-between px-4 py-3 border-b">
                  <h3 className="text-base font-semibold text-gray-800">
                    Configuración permisos de tarjetas
                  </h3>
                  <button
                    type="button"
                    className="btn-fil"
                    onClick={() => setMostrarPermisos(false)}
                  >
                    Cerrar
                  </button>
                </div>

                <div className="h-[calc(90vh-56px)] overflow-auto">
                  <PermisoTarjeta
                    key={tarjetaSel?.numero || tarjetaSel?.id}
                    tarjetaId={tarjetaSel?.id}
                    numeroTarjeta={tarjetaSel?.numero}
                    clienteNombre={tarjetaSel?.clienteNombre}
                    chapa={tarjetaSel?.chapa}
                    vehiculoMarca={tarjetaSel?.vehiculoMarca}
                    vehiculoEtiqueta={tarjetaSel?.vehiculoEtiqueta}
                    onVolver={() => setMostrarPermisos(false)}
                  />
                </div>
              </div>
            </div>
          </div>
        </ModalPortal>
      )}
    </div>
  );
}

export default TarjetaPrepaga;